import React from 'react'
import {
    Typography,
    TextField,
    Stack,
    Button,
    Switch,
    MenuItem,
    Select,
    Box,
    IconButton,
    Menu,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material'
import { VpnKeyOutlined, DeleteOutlined, MailOutline, RestoreOutlined, MoreVert } from '@mui/icons-material'
import { AxiosWithAuth, notifyStoreEmploymentsChanged } from './authenticationService'
import { ROLE_MAPPINGS, PART_FULLTIME_MAPPINGS, OPEN_CLOSE_MAPPINGS } from '../constants'
import ButtonWithConfirmPrompt from '../Components/ButtonWithConfirmPrompt'
import { PATHS } from '../constants'
import { isMobile } from './isMobile'
import {
    validateHiraganaKatakana,
    validateHiraganaKatakanaOptional,
    validateKanaNicknameOptional,
    validateKanjiHiraganaKatakanaOptional,
    validateEmail,
    validatePhone,
    validateEmpCode,
    KANA_NAME_HELPER_TEXT
} from './fieldValidators'

export const adminUserEditPath = (employmentId, userEmpId, userId) =>
    PATHS.adminUserManagementEdit
        .replace(':employmentId', String(employmentId ?? ''))
        .replace(':userEmpId', String(userEmpId))
        .replace(':userId', String(userId))

/** Base path for UserSearch (ends with slash; UserSearch appends user.id) */
export const adminUserEditSearchPath = (employmentId) =>
    adminUserEditPath(employmentId, 'noEmployment', '').replace(/\/$/, '') + '/'

// Common field validators and error messages
export const fieldValidators = {
    name_last: validateHiraganaKatakana,
    name_first: validateHiraganaKatakana,
    kanji_last: validateKanjiHiraganaKatakanaOptional,
    kanji_first: validateKanjiHiraganaKatakanaOptional,
    name_middle: validateHiraganaKatakanaOptional,
    nickname: validateKanaNicknameOptional,
    email: validateEmail,
    phone: validatePhone,
    emp_cd: validateEmpCode
}

export const fieldErrorText = {
    name_last: KANA_NAME_HELPER_TEXT,
    name_first: KANA_NAME_HELPER_TEXT,
    kanji_last: '全角漢字・全角ひらがな・全角カタカナ',
    kanji_first: '全角漢字・全角ひらがな・全角カタカナ',
    name_middle: KANA_NAME_HELPER_TEXT,
    nickname: KANA_NAME_HELPER_TEXT,
    email: '半角英数字',
    phone: '半角数字',
    emp_cd: '半角数字'
}

// Common text field component
export const getTextField = (label, key, info, setInfo, errors, setErrors, disable = false) => {
    return (
        <TextField
            label={label}
            value={info[key] ?? ''}
            onChange={e => {
                if (!fieldValidators[key](e.target.value)) {
                    setErrors({ ...errors, [key]: fieldErrorText[key] })
                } else {
                    setErrors({ ...errors, [key]: '' })
                }
                setInfo({ ...info, [key]: e.target.value })            
            }}
            sx={{ width: isMobile() ? '90%' : '50%' }}
            error={errors[key]}
            helperText={errors[key]}
            disabled={disable}
            size={isMobile() ? 'small' : 'medium'}
        />
    )
}

// Employee code field with optional prefix for non-default companies
export const getEmpCdField = (info, setInfo, errors, setErrors, employments, storeList) => {
    const firstEmployment = employments?.[0]
    const storeId = firstEmployment?.store_id || info?.store_id
    const store = storeList?.find(s => s.id === storeId)
    const isDefaultCompany = store?.company?.default_company === true
    const segCode = store?.seg_code
    const empCd = String(info?.emp_cd ?? '')
    const hasPrefix = segCode && empCd.toLowerCase().startsWith(`${segCode.toLowerCase()}-`)
    
    if (!isDefaultCompany && hasPrefix && segCode) {
        const numericPart = empCd.substring(segCode.length + 1)
        return (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', width: isMobile() ? '90%' : '50%' }}>
                <TextField
                    label="社員コード"
                    value={segCode.toUpperCase() + '-'}
                    variant="outlined"
                    disabled
                    sx={{ 
                        width: '35%',
                        mr: '0.5rem', 
                        '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } 
                    }}
                    size={isMobile() ? 'small' : 'medium'}
                />
                <TextField
                    value={numericPart}
                    placeholder="例：0000000"
                    variant="outlined"
                    sx={{ width: '65%' }}
                    error={!!errors?.emp_cd}
                    helperText={errors?.emp_cd}
                    inputProps={{ 'aria-label': '社員コードの数字部分' }}
                    onChange={(e) => {
                        const numericValue = e.target.value
                        const fullEmpCd = `${segCode.toUpperCase()}-${numericValue}`
                        if (!fieldValidators.emp_cd(numericValue)) {
                            setErrors({ ...errors, emp_cd: fieldErrorText.emp_cd })
                        } else {
                            setErrors({ ...errors, emp_cd: '' })
                        }
                        setInfo({ ...info, emp_cd: fullEmpCd })
                    }}
                    size={isMobile() ? 'small' : 'medium'}
                />
            </Box>
        )
    }
    return getTextField('社員コード', 'emp_cd', info, setInfo, errors, setErrors)
}

// Common store text field component
export const getStoreTextField = (label, key, info, storeList, setOpenEditStores) => {
    const selectedStores = storeList.filter(store => info[key]?.includes(store.id))
    return (
        <TextField
            label={label}
            value={selectedStores.map(store => store.name).join(', ')}
            onClick={() => {
                setOpenEditStores(true)
            }}
            sx={{ width: isMobile() ? '90%' : '50%' }}
        />
    )
}

// Common number field component
// options.nonNegativeInteger: only allow '' or digits 0-9 in state (no -, ., e); use for integer DB columns.
export const getNumberField = (label, key, info, setInfo, disable = false, inputProps = {}, options = {}) => {
    const { nonNegativeInteger } = options
    return (
        <TextField
            label={label}
            value={info[key] ?? ''}
            type="number"
            sx={{ width: isMobile() ? '90%' : '50%' }}
            onChange={e => {
                const v = e.target.value
                if (nonNegativeInteger) {
                    if (v === '' || /^[0-9]*$/.test(v)) {
                        setInfo({ ...info, [key]: v })
                    }
                } else {
                    setInfo({ ...info, [key]: v })
                }
            }}
            disabled={disable}
            inputProps={inputProps}
            size={isMobile() ? 'small' : 'medium'}
        />
    )
}

// Common employment type select component
export const getEmploymentTypeSelect = (info, setInfo, canEditTargetUser) => {
    return (
        <Select
            id="eType"
            label="雇用形態"
            placeholder="選択してください"
            value={info.e_type || ''}
            onChange={event =>
                canEditTargetUser() && setInfo({ ...info, e_type: event.target.value })
            }
            sx={{ width: isMobile() ? '90%' : '49%' }}
            disabled={!canEditTargetUser()}
        >
            {Object.keys(PART_FULLTIME_MAPPINGS).map(key => (
                <MenuItem key={key} value={key}>
                    {PART_FULLTIME_MAPPINGS[key]}
                </MenuItem>
            ))}
        </Select>
    )
}

// MUI Select values are strings ("true"/"false"); API returns booleans. Avoid `false || ''` which clears valid "not closer" state.
const openCloseDutySelectValue = value =>
    value === null || value === undefined ? '' : String(value)

const employmentsWithNormalizedOpenCloseDuty = employments =>
    (employments || []).map(emp => ({
        ...emp,
        open_close_duty: openCloseDutySelectValue(emp.open_close_duty)
    }))

// Common employment card component
export const EmploymentCard = ({ 
    employment, 
    employments, 
    setEmployments, 
    info, 
    setSnackbarMessage,
    setSnackbarSeverity,
    onDelete,
    employmentId
}) => {
    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="header1" sx={{ fontSize: '20px' }}>
                    {employment.store.name}
                </Typography>
                {onDelete && (
                    <ButtonWithConfirmPrompt
                        component={
                            <IconButton
                                size="small"
                                disabled={String(employment.id) === String(employmentId)}
                                sx={{ color: 'error.main' }}
                            >
                                <DeleteOutlined/>
                            </IconButton>
                        }
                        title="店舗の削除"
                        content="このユーザーを店舗雇用リストから外しますか？なお、一度外すと元に戻すことはできません。また、このユーザーのこの店舗におけるシフトも全て削除されます。"
                        confirmText="削除する"
                        cancelText="キャンセル"
                        handleConfirm={() => onDelete(employment)}
                    />
                )}
            </div>
            <div style={{ marginTop: '.5rem', borderBottom: '1px solid #e0e0e0' }} />
            <Stack spacing={2} style={{ margin: '1rem' }}>
                <TextField
                    select
                    id="kitchen/hall"
                    label="勤務タイプ"
                    variant="standard"
                    placeholder="選択してください"
                    value={employment.role || ''}
                    onChange={event =>
                        setEmployments(
                            employments.map(emp =>
                                emp.id === employment.id ? { ...emp, role: event.target.value } : emp
                            )
                        )
                    }
                >
                    {Object.keys(ROLE_MAPPINGS).map(key => (
                        <MenuItem key={key} value={key}>
                            {ROLE_MAPPINGS[key]}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    id="open/close"
                    label="開閉担当者"
                    variant="standard"
                    placeholder="選択してください"
                    value={openCloseDutySelectValue(employment.open_close_duty)}
                    onChange={event =>
                        setEmployments(
                            employments.map(emp =>
                                emp.id === employment.id ? { ...emp, open_close_duty: event.target.value } : emp
                            )
                        )
                    }
                >
                    {Object.keys(OPEN_CLOSE_MAPPINGS).map(key => (
                        <MenuItem key={key} value={key}>
                            {' '}
                            {OPEN_CLOSE_MAPPINGS[key]}{' '}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    id="rank"
                    label="ランク"
                    variant="standard"
                    placeholder="選択してください"
                    value={employment.rank || ''}
                    onChange={event =>
                        setEmployments(
                            employments.map(emp =>
                                emp.id === employment.id ? { ...emp, rank: event.target.value } : emp
                            )
                        )
                    }
                >
                    {['A', 'B', 'T'].map(key => (
                        <MenuItem key={key} value={key}>
                            {key}
                        </MenuItem>
                    ))}
                </TextField>
            </Stack>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    margin: '0 1rem'
                }}
            >
                <Typography variant="subtitle1">シフト更新権限</Typography>
                <Switch
                    checked={employment.can_edit_shift}
                    onChange={event => {
                        if (employment.support_staff) {
                            setSnackbarSeverity?.('warning')
                            setSnackbarMessage('サポートスタッフはシフト更新権限を持つことができません。')
                            return
                        }
                        setEmployments(
                            employments.map(emp =>
                                emp.id === employment.id ? { ...emp, can_edit_shift: event.target.checked } : emp
                            )
                        )
                    }}
                />
            </div>
            <Typography
                variant="body2"
                style={{
                    margin: '0 1rem',
                    marginBottom: '1rem',
                    marginRight: '2rem',
                    color: 'grey'
                }}
            >
                シフト更新権限があるユーザーはシフトを作成することができます。
            </Typography>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    margin: '0 1rem'
                }}
            >
                <Typography variant="subtitle1">マネージャー</Typography>
                <Switch
                    checked={employment.is_manager}
                    disabled={info.e_type != 'full-time'}
                    onChange={event => {
                        if (employment.support_staff) {
                            setSnackbarSeverity?.('warning')
                            setSnackbarMessage('サポートスタッフはマネージャーにできません。')
                            return
                        }
                        setEmployments(
                            employments.map(emp =>
                                emp.id === employment.id ? { ...emp, is_manager: event.target.checked } : emp
                            )
                        )
                    }}
                />
            </div>
            <Typography
                variant="body2"
                style={{
                    margin: '0 1rem',
                    marginBottom: '1rem',
                    marginRight: '2rem',
                    color: 'grey'
                }}
            >
                正社員のみマネージャーにできます。
            </Typography>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    margin: '0 1rem',
                    marginBottom: '3rem'
                }}
            >
                <Typography variant="subtitle1">サポートスタッフ</Typography>
                <Switch
                    checked={employment.support_staff}
                    onChange={event => {
                        const checked = event.target.checked
                        setEmployments(
                            employments.map(emp =>
                                emp.id === employment.id ? { ...emp, support_staff: checked, 
                                                            is_manager: checked ? false : emp.is_manager, 
                                                            can_edit_shift: checked ? false : emp.can_edit_shift } : emp
                            )
                        )
                    }}
                />
            </div>
        </div>
    )
}

// Common admin/manager buttons component
export const UserActionMenu = ({
    info,
    userId,
    setSnackbarMessage,
    setSnackbarSeverity,
    fetch,
    setIsLoading,
    history,
    isAdmin
}) => {
    const [anchorEl, setAnchorEl] = React.useState(null)
    const [confirmDialog, setConfirmDialog] = React.useState(null)

    const menuOpen = Boolean(anchorEl)

    const handleMenuOpen = (e) => setAnchorEl(e.currentTarget)
    const handleMenuClose = () => setAnchorEl(null)
    const handleDialogClose = () => setConfirmDialog(null)

    const openDialog = (type) => {
        handleMenuClose()
        setConfirmDialog(type)
    }

    const dialogConfigs = {
        deactivate: {
            title: 'ユーザーの無効化',
            content: 'アカウントを無効にすると、このユーザーはログインできなくなります。このユーザーを無効にしてもよろしいですか？',
            confirmText: '無効にする',
            handleConfirm: () => {
                AxiosWithAuth.post('/users/deactivate/' + userId).then(res => {
                    if (res.status === 200 && res.data.message === "User deactivated") {
                        setSnackbarSeverity?.('success')
                        setSnackbarMessage('ユーザーを無効にしました。')
                        notifyStoreEmploymentsChanged()
                        fetch()
                    } else {
                        setSnackbarSeverity?.('error')
                        setSnackbarMessage('ユーザーを無効にできませんでした。')
                    }
                })
            }
        },
        reactivate: {
            title: 'ユーザーの有効化',
            content: 'このユーザーのアカウントを有効にし、アーカイブされたシフトを復元します。この操作を実行しますか？',
            confirmText: '有効にする',
            handleConfirm: () => {
                AxiosWithAuth.post('/users/reactivate/' + userId).then(res => {
                    if (res.status === 200 && res.data.message === "User reactivated") {
                        setSnackbarSeverity?.('success')
                        setSnackbarMessage('ユーザーを有効にしました。')
                        fetch()
                    } else {
                        setSnackbarSeverity?.('error')
                        setSnackbarMessage('ユーザーを有効にできませんでした。')
                    }
                }).catch(() => {
                    setSnackbarSeverity?.('error')
                    setSnackbarMessage('ユーザーを有効にできませんでした。')
                })
            }
        },
        delete: {
            title: 'ユーザーの削除',
            content: 'アカウントを削除すると過去のデータはすべて削除され、復元することはできません。このユーザーのアカウントを完全に削除してもよろしいですか？',
            confirmText: '削除する',
            handleConfirm: () => {
                setIsLoading(true)
                const currPath = history.location.pathname
                const newPath = currPath.includes('admin') ? PATHS.adminUserManagements : PATHS.managerSettingsUsers
                AxiosWithAuth.delete('/users/' + userId).then(res => {
                    res.status === 204 &&
                        history.push({ pathname: newPath, state: { deleted: userId } })
                    setIsLoading(false)
                })
            }
        }
    }

    const active = confirmDialog ? dialogConfigs[confirmDialog] : null

    return (
        <>
            <IconButton onClick={handleMenuOpen} size="small">
                <MoreVert />
            </IconButton>
            <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleMenuClose}>
                {!info.deactivated_at ? (
                    <MenuItem onClick={() => openDialog('deactivate')} sx={{ color: 'error.main' }}>
                        <DeleteOutlined fontSize="small" sx={{ mr: 1 }} />
                        このユーザーのアカウントを無効にする
                    </MenuItem>
                ) : isAdmin && (
                    <MenuItem onClick={() => openDialog('reactivate')} sx={{ color: 'success.main' }}>
                        <RestoreOutlined fontSize="small" sx={{ mr: 1 }} />
                        このユーザーのアカウントを有効にする
                    </MenuItem>
                )}
                {isAdmin && (
                    <MenuItem onClick={() => openDialog('delete')} sx={{ color: 'error.main' }}>
                        <DeleteOutlined fontSize="small" sx={{ mr: 1 }} />
                        このユーザーのアカウントを削除
                    </MenuItem>
                )}
            </Menu>
            {active && (
                <Dialog open={true} onClose={handleDialogClose}>
                    <DialogTitle>{active.title}</DialogTitle>
                    <DialogContent>{active.content}</DialogContent>
                    <DialogActions>
                        <Button onClick={handleDialogClose}>キャンセル</Button>
                        <Button
                            color="error"
                            onClick={() => {
                                handleDialogClose()
                                active.handleConfirm()
                            }}
                        >
                            {active.confirmText}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    )
}

export const AdminManagerButtons = ({ 
    info, 
    setSnackbarMessage,
    setSnackbarSeverity,
    fetch
}) => {
    return (
        <>
            {!info.confirmed_at && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: '100%' }}>
                    <ButtonWithConfirmPrompt
                        component={
                            <Button variant="outlined" color="paloBlue" sx={{ mb: '1rem', alignSelf: { xs: 'stretch', sm: 'auto' }, whiteSpace: 'normal', textAlign: 'center' }}>
                                <MailOutline sx={{ mr: '.5rem' }} />
                                メールアドレスを代理で確認
                            </Button>
                        }
                        title="メールアドレスを代理で確認"
                        content="このユーザーのメールアドレスを代理で確認しますか？"
                        confirmText="確認する"
                        handleConfirm={() => {
                            AxiosWithAuth.post('/confirmUser/', { id: info.id }).then(res => {
                                console.log(res.data)
                                if (res.status === 200) {
                                    setSnackbarSeverity?.('success')
                                    setSnackbarMessage('メールアドレスの本人確認が完了しました。')
                                    fetch()
                                }
                            })
                        }}
                    />
                </Stack>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: '100%' }}>
                <ButtonWithConfirmPrompt
                    component={
                        <Button variant="outlined" color="paloBlue" sx={{ mb: '1rem', alignSelf: { xs: 'stretch', sm: 'auto' }, whiteSpace: 'normal', textAlign: 'center' }}>
                            <VpnKeyOutlined sx={{ mr: '.5rem' }} />
                            このユーザーのパスワードをリセット
                        </Button>
                    }
                    title="パスワードのリセット"
                    content="このユーザーのパスワードをリセットしますか？"
                    confirmText="リセットする"
                    handleConfirm={() => {
                        AxiosWithAuth.post('/resetPassword', { id: info.id })
                            .then(res => {
                                if (res.status === 200) {
                                    setSnackbarSeverity?.('success')
                                    setSnackbarMessage('パスワードをリセットし、ユーザーにメールで送信しました。')
                                }
                            })
                            .catch(() => {
                                setSnackbarSeverity?.('error')
                                setSnackbarMessage('パスワードのリセットに失敗しました。')
                            })
                    }}
                />
            </Stack>
        </>
    )
}

// Common save handler
export const handleSave = (employments, originalEmployments, info, originalInfo, errors, setErrors, setSnackbarMessage, userId) => {
    if (Object.values(errors).some(e => e)) {
        return
    }
    
    // Save employment changes
    employments.forEach((e, i) => {
        const originalEmployment = originalEmployments[i]
        const updatedEmployment = Object.keys(e).reduce((acc, key) => {
            if (e[key] !== originalEmployment[key]) {
                acc[key] = e[key]
            }
            return acc
        }, {})
        if (Object.keys(updatedEmployment).length) {
            AxiosWithAuth.put('/employments/' + e.id, {
                ...updatedEmployment
            })
                .then(res => {
                    res.status === 200 && setSnackbarMessage('変更を保存しました。')
                })
                .catch(error => {
                    const errors = {}
                    for (const [key, value] of Object.entries(error?.response?.data)) {
                        errors[key] = value.join(' ')
                    }
                    setErrors(errors)
                })
        }
    })
    
    // Save user info changes
    const updatedInfo = Object.keys(info).reduce((acc, key) => {
        if (info[key] !== originalInfo[key]) {
            acc[key] = info[key]
        }
        return acc
    }, {})
    if (Object.keys(updatedInfo).length) {
        AxiosWithAuth.put('/users/' + userId, {
            ...updatedInfo
        })
            .then(res => {
                if (res.status === 200) {
                    setSnackbarMessage('変更を保存しました。');
                }
            })
            .catch(error => {
                const errors = {}
                for (const [key, value] of Object.entries(error?.response?.data)) {
                    errors[key] = value.join(' ')
                }
                setErrors(errors)
            })
    }
}

// Common fetch function
export const fetchUserData = async (userId, userEmpId, setInfo, setOriginalInfo, setEmployments, setOriginalEmployments, setStoreList) => {
    const userInfo = await AxiosWithAuth.get('/users/' + userId)
    const employmentInfo = userEmpId !== 'noEmployment' ? await AxiosWithAuth.get('/employments/' + userEmpId) : {}
    userInfo.data.employments?.length && (userInfo.data.store_ids = userInfo.data.employments.map(e => e.store_id))
    const userEmploymentsInfo = await AxiosWithAuth.get('/employments/?user_id=' + userId)
    await AxiosWithAuth.get('/stores').then(res => setStoreList(res.data))
    const sortedEmployments = [...(userEmploymentsInfo.data || [])].sort((a, b) => a.id - b.id)
    const normalized = employmentsWithNormalizedOpenCloseDuty(sortedEmployments)
    setEmployments(normalized.map(emp => ({ ...emp })))
    setOriginalEmployments(normalized.map(emp => ({ ...emp })))
    const infoToAdd =
        userEmpId === 'noEmployment' ? normalized[0] || {} : employmentInfo.data
    setInfo({ ...infoToAdd, ...userInfo.data })
    setOriginalInfo({ ...infoToAdd, ...userInfo.data })
}

// Common editing state effect
export const useEditingState = (info, originalInfo, employments, originalEmployments, setEditing) => {
    React.useEffect(() => {
        if (info && originalInfo) {
            const updated = Object.keys(info).some(key => info[key] !== originalInfo[key])
            const updatedEmployments = employments?.some((e, i) => {
                const originalEmployment = originalEmployments?.[i]
                if (!originalEmployment) return false
                return Object.keys(e).some(key => e[key] !== originalEmployment[key])
            })
            if (updated || updatedEmployments) {
                setEditing(true)
            } else {
                setEditing(false)
            }
        }
    }, [info, originalInfo, employments, originalEmployments])
}
